import gc_i18n from "gc_i18n";
import iView from "view-design";
import ProIview from "pro_iview";
import Vue from "vue";
import store from "store2";
import { getI18nTokenInfo } from "@/service/user";
import { getServerConfigValue } from "@/service/server";

let gcI18nInstance;
let iViewInstalled = false;
let proIviewInstalled = false;
let i18nAuthRequest;
const i18nAuthInfo = {
  appCode: "IWORK",
  orgCode: "",
  userCode: "",
  tempToken: "",
  expiresAt: 0,
};
// 保存 IAM 返回的完整翻译身份；orgCode 在业务侧对应当前单位的 unitCode。
const cacheI18nAuthInfo = (info = {}) => {
  const tempToken = info.temp_token || info.tempToken || "";
  const expiresIn = Number(info.expires_in || info.expiresIn || 0);
  Object.assign(i18nAuthInfo, {
    appCode: info.appCode || "IWORK",
    orgCode: info.orgCode || "",
    userCode: info.userCode || "",
    tempToken,
    // 提前 10 秒失效，避免翻译请求发出时令牌刚好过期。
    expiresAt: expiresIn ? Date.now() + Math.max(expiresIn - 10, 0) * 1000 : 0,
  });
  return Boolean(
    i18nAuthInfo.tempToken &&
      i18nAuthInfo.appCode &&
      i18nAuthInfo.orgCode &&
      i18nAuthInfo.userCode
  );
};

const hasValidI18nToken = () =>
  Boolean(
    i18nAuthInfo.tempToken &&
      (!i18nAuthInfo.expiresAt || Date.now() < i18nAuthInfo.expiresAt)
  );

// 会话初始化流程发生重叠时复用同一个请求，避免并发获取重复的临时令牌。
const fetchI18nAuthInfo = async () => {
  if (i18nAuthRequest) {
    return i18nAuthRequest;
  }
  i18nAuthRequest = getI18nTokenInfo()
    .then(
      (res) =>
        Boolean(res.data && res.data.result === 0) &&
        cacheI18nAuthInfo(res.data.retVal)
    )
    .catch(() => false)
    .finally(() => {
      i18nAuthRequest = null;
    });
  return i18nAuthRequest;
};

// gc_i18n 只读取已获取且仍有效的令牌，不能从普通翻译请求隐式触发 /getitt。
const requestI18nToken = async () => {
  if (hasValidI18nToken()) {
    return i18nAuthInfo.tempToken;
  }
  return "";
};

// 登录进入单位或登录态刷新时主动更新完整翻译身份，普通用户信息查询不触发。
export const refreshI18nAuthInfo = async () => {
  if (!(await fetchI18nAuthInfo())) {
    return false;
  }
  if (!gcI18nInstance) {
    return false;
  }
  const { appCode, orgCode, userCode } = i18nAuthInfo;
  return gcI18nInstance.setToken("", {
    appCode,
    orgCode,
    userCode,
    cacheScope: `${orgCode}_${userCode}`,
  });
};

const selectedLanguage = store.get("language");

// 页面语言持久化在 language，gc_i18n 则读取 I18N_LANGUAGE。刷新前同步两者，
// 避免语言选择器显示 en-US 而 gc_i18n 仍以过期的 zh-CN 初始化。
if (selectedLanguage) {
  store.set("I18N_LANGUAGE", selectedLanguage);
}

export default {
  // 接入 gc_i18n 后，统一给 Vue 2 与现有组件库提供翻译函数。
  // router/http 由 main.js 在基础模块加载完成后传入，避免初始化阶段形成循环依赖。
  install(app, router, http) {
    if (!gcI18nInstance) {
      // install 在 server.json 初始化完成后执行，此处读取部署环境对应的翻译服务配置。
      const env = getServerConfigValue("GC_I18N_ENV");
      console.log("env------", env);
      gcI18nInstance = new gc_i18n({
        appCode: "IWORK",
        keyboard: "ctrl+shift+alt+t",
        router,
        env,
        vue: Vue,
        locale: selectedLanguage,
        auth: {
          getScope: () => {
            const { orgCode: unitCode, userCode } = i18nAuthInfo;
            return unitCode && userCode ? `${unitCode}_${userCode}` : "";
          },
          getOrgCode: () => i18nAuthInfo.orgCode,
          getUserCode: () => i18nAuthInfo.userCode,
          isAuthenticated: () => {
            const { orgCode: unitCode, userCode } = i18nAuthInfo;
            return !!(unitCode && userCode);
          },
        },
        i18nTokenRequest: requestI18nToken,
      });
    } else if (router && typeof gcI18nInstance.setRouter === "function") {
      // 首次安装发生在 router 加载前，路由就绪后补回守卫和语言切换能力。
      gcI18nInstance.setRouter(router);
    }
    const { i18n } = gcI18nInstance;
    if (!iViewInstalled) {
      Vue.use(iView, {
        // 关键配置：i18n 函数，参数为 key 和 value（iView 会自动传参）
        i18n: (key, value) => i18n.global.t(key, value),
      });
      iViewInstalled = true;
    }
    if (http && !proIviewInstalled) {
      // pro_iview 与 iView 共用 gc_i18n 已加载的语言包。
      Vue.use(ProIview, {
        http,
        i18n: (key, value) => i18n.global.t(key, value),
      });
      proIviewInstalled = true;
    }
    // vue-i18n 8 在 Vue.prototype.$i18n 上定义只读属性，使用 Vue 3 形状的适配对象完成 gc_i18n 注册，避免覆盖该属性。
    if (app && app.prototype && !app.prototype.$t) {
      gcI18nInstance.install(app);
    }
  },
};
